import React, { useState } from 'react';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  toTitleCase,
  isRouteParam,
  extractRouteParameters,
  buildRouteUrl,
} from '../../utils/breadcrumb.utils';
import {
  ROUTE_LABELS,
  ROUTE_HIERARCHIES,
  type RouteHierarchyConfig,
} from '../../utils/breadcrumb.config';

interface DynamicBreadcrumbsProps {
  isLoading?: boolean;
  onNavigate?: (path: string) => boolean;
}

const DynamicBreadcrumbs: React.FC<DynamicBreadcrumbsProps> = ({onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathSegments = location.pathname.split('/').filter(Boolean);
   const [_isNavigating, setIsNavigating] = useState(false);

   const breadcrumbsDisabled = Boolean(
    (location.state as { breadcrumbsDisabled?: boolean } | null)?.breadcrumbsDisabled
  );

  const findMatchingHierarchy = (): RouteHierarchyConfig | null => {
    const breadcrumbSource = (location.state as { breadcrumbSource?: string })?.breadcrumbSource;
    let fallbackConfig: RouteHierarchyConfig | null = null;
    
    for (const [routeKey, config] of Object.entries(ROUTE_HIERARCHIES)) {
      const [baseRouteKey, stateKey] = routeKey.split(':');
      const routeParts = baseRouteKey.split('/').filter(Boolean);
      const pathParts = pathSegments.filter((p) => !isRouteParam(p));

      const routeMatches = routeParts.every((part, i) => part.startsWith(':') || part === pathParts[i]);
      
      if (routeMatches) {
        if (stateKey) {
          if (breadcrumbSource === stateKey) {
            return config;
          }
          if (!fallbackConfig) {
            fallbackConfig = config;
          }
        } else {
          return config;
        }
      }
    }
    return fallbackConfig;
  };

  const hierarchyConfig = findMatchingHierarchy();

  if (pathSegments.length <= 1) return null;
  
  const segments = hierarchyConfig?.segments || pathSegments.filter((s) => !isRouteParam(s));


  const resolveSegmentLabel = (segment: string): string => {
    if (segment.startsWith(':')) {
      const paramName = segment.substring(1);
      if (hierarchyConfig) {
        for (const pattern of Object.values(hierarchyConfig.routePatterns)) {
          const paramNames = hierarchyConfig.parameterMappings?.[segment] || [paramName];
          const extracted = extractRouteParameters(location.pathname, pattern, paramNames);
          if (extracted[paramName]) return extracted[paramName];
        }
      }

      const paramSegments = pathSegments.filter((s) => isRouteParam(s));
      return paramSegments[0] || segment;
    }
    return ROUTE_LABELS[segment] ?? toTitleCase(segment);
  };

  const getNavigationPath = (index: number): string => {
    const segment = segments[index];
    const pattern = hierarchyConfig?.routePatterns[segment];

    if (!pattern) return '/' + segments.slice(0, index + 1).join('/');

    const paramNames = hierarchyConfig.parameterMappings?.[segment] || [];
    if (paramNames.length === 0) return pattern;

    const params: Record<string, string> = {};

    if (hierarchyConfig) {
      const allPatterns = Object.values(hierarchyConfig.routePatterns);
      const currentPathParts = location.pathname.split('/').filter(Boolean);

      for (const availablePattern of allPatterns) {
        const extractedParams = extractRouteParameters(
          location.pathname,
          availablePattern,
          paramNames
        );
        Object.entries(extractedParams).forEach(([key, value]) => {
          if (value && value !== '' && paramNames.includes(key)) {
            params[key] = value;
          }
        });
      }

      if (Object.keys(params).length === 0) {
        const patternParts = pattern.split('/').filter(Boolean);
        patternParts.forEach((patternPart, _patternIndex) => {
          if (patternPart.startsWith(':')) {
            const paramName = patternPart.substring(1);
            if (paramNames.includes(paramName)) {
              currentPathParts.forEach((pathPart) => {
                if (!isRouteParam(pathPart)) return;
                if (!params[paramName]) {
                  params[paramName] = pathPart;
                }
              });
            }
          }
        });
      }
    }

    return buildRouteUrl(pattern, params);
    };

  const handleNavigation = async (index: number) => {
    if (breadcrumbsDisabled) return;

    setIsNavigating(true);
    const path = getNavigationPath(index);
    if (onNavigate) {
      const shouldNavigate = await Promise.resolve(onNavigate(path));
      if (shouldNavigate) {
        navigate(path, { state: { ...location.state, fromBreadcrumb: true } });
        setIsNavigating(false);
      } else {
        setIsNavigating(false);
      }
    } else {
      navigate(path, { state: { ...location.state, fromBreadcrumb: true } });
      setIsNavigating(false);
    }
  };


  return (
    <Box sx={{ m: 2 }}>
      <Breadcrumbs
        separator={
          breadcrumbsDisabled ? (
            <NavigateNextIcon sx={{ color: '#a09c9c' }} fontSize="small" />
          ) : (
            <NavigateNextIcon fontSize="small" />
          )
        }
      >
        {segments.map((segment, index) => {
          const isLast = index === segments.length - 1;
          const label = resolveSegmentLabel(segment);

          return isLast ? (
            <Typography key={index} sx={{ fontWeight: 600, color: 'text.primary' }}>
              {label}
            </Typography>
          ) : (
            <Link
              key={index}
              color="primary"
              sx={{
                cursor: breadcrumbsDisabled ? 'default' : 'pointer',
                fontWeight: 500,
                pointerEvents: breadcrumbsDisabled ? 'none' : 'auto',
                opacity: breadcrumbsDisabled ? 0.6 : 1,
              }}
              onClick={() => handleNavigation(index)}
            >
              {label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default DynamicBreadcrumbs;
